import React from 'react';
import { useSelector } from 'react-redux';
import {
  getActiveDashboard,
  getDashboardsEnabled,
  getDashboardsState,
  getIsSelfHosted,
  getUserActions,
} from '@/store-selectors';
import Dashboard_LEGACY from '@/components/Dashboard';
import { useDashboardId, useDatasourceId, useEventListener } from '@/hooks';
import flex from '@/css/module/flex.css';
import AjaxSpinner from '@/components/AjaxSpinner';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectChartWidgetIndex,
  selectWidget,
} from '@/features/dashboard/dashboardSlice';
import {
  chartConfigurationApi,
  QUERY_AVAILABLE_CHARTS,
} from '@/features/chart-configuration/apiSlice';
import ViewRef from '@/util/ViewRef';
import {
  fireViewWithFilters,
  loadDashboard,
  modalInteraction,
  refreshDashboard,
  refreshWidgets,
  saveDashboard,
} from '@/actions';
import DashboardRef from '@/util/DashboardRef';
import { DASHBOARDS_VIEW } from '@/util/enums/EnumModals';
import { Permission } from '@/permissions/Permission';
import { DASHBOARD_PREVIEW_WIDGET_CREATED } from '@/constants/custom-event-names';
import { DashboardsContainer } from '@/views/DashboardList/Dashboards.styles';
import { useSearchParams } from 'react-router-dom';
import { setLastVisitedDashboardId } from '@/features/dashboards/dashboardsSlice';
import { DASHBOARD_CONTAINER } from '@/constants/test-ids';
import { PRIVATE_DASHBOARD } from '@/features/dashboard/types';
import { RefreshTimer } from './components/RefreshTimer';

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();

  const datasourceId = useDatasourceId();
  const dashboardId = useDashboardId();
  const [searchParams] = useSearchParams();

  const dashboardsState = useSelector(getDashboardsState);
  const activeDashboardId = useSelector(getActiveDashboard)?._id;
  const userActions = useSelector(getUserActions);
  const restricted = !useSelector(getDashboardsEnabled);
  const isSelfHosted = useSelector(getIsSelfHosted);
  const widgetBeingEdited = useAppSelector(selectChartWidgetIndex);

  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    dispatch(setLastVisitedDashboardId(dashboardId));
  }, [dashboardId]);

  React.useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo(0, 0);
    }
  }, [containerRef]);

  React.useEffect(() => {
    if (dashboardId === activeDashboardId) return;

    dispatch(
      loadDashboard(
        new DashboardRef(
          dashboardId,
          searchParams.get('dr'),
          'Loading...',
          null,
        ),
      ),
    );
  }, [dashboardId, activeDashboardId]);

  const handleScrollTopOnPreviewWidgetCreated = React.useCallback(
    (e: Event) => {
      const container = containerRef.current;
      const { top, height } = (e as CustomEvent).detail;

      if (container) {
        const containerScrollOffsetX =
          container.offsetHeight + container.scrollTop;
        const widgetBottomX = top + height + 50;

        setTimeout(() => {
          container.scrollTo({
            behavior: 'smooth',
            top:
              containerScrollOffsetX > widgetBottomX
                ? container.scrollTop
                : widgetBottomX,
          });
        }, 600);
      }
    },
    [containerRef],
  );

  useEventListener(
    DASHBOARD_PREVIEW_WIDGET_CREATED,
    handleScrollTopOnPreviewWidgetCreated,
  );

  const onConfigureChart =
    // @ts-expect-error: infer types.


      (view, widget, dataTable, chartConfiguration, index) =>
      (e: React.MouseEvent) => {
        e.preventDefault();
        dispatch(
          chartConfigurationApi.util.invalidateTags([QUERY_AVAILABLE_CHARTS]),
        );
        dispatch(
          selectWidget({
            view,
            selectedChartWidgetIndex: index,
            displayType: widget.displayType,
            dataTable,
            chartConfiguration: widget.chartSetting || chartConfiguration,
            widgetTitle: widget.widgetTitle,
            widget,
          }),
        );
      };
  const onOpenViewInNewTab = (view: ViewRef) => (e: React.MouseEvent) => {
    e.preventDefault();
    window.open(
      `/datasources/${datasourceId}/saved-queries/${view.series()}`,
      '_blank',
    );
  };

  const onOpenViewWithFilters =
    (view: ViewRef, appliedFilters: unknown[], dashboard: unknown) =>
    (e: React.MouseEvent) => {
      e.preventDefault();
      dispatch(
        fireViewWithFilters(
          view.series(),
          appliedFilters,
          dashboard,
          `/datasources/${datasourceId}`,
        ),
      );
    };

  const onSave = (state: unknown) => (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(saveDashboard(state));
  };

  const onDiscard = (dashboard: unknown) => (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(loadDashboard(dashboard));
  };

  const onAwaitingWidgetData = (incrementRetryTime: boolean | undefined) => {
    dispatch(refreshWidgets(incrementRetryTime));
  };

  const onRefreshClicked =
    (dashboard: DashboardRef) =>
    (e: unknown, _filtersBefore: unknown[], filtersAfter: Maybe<unknown[]>) => {
      if (filtersAfter?.length === 0) {
        // user has removed all applied filters, so we reload the dashboard here rather than actually kicking off a server-side refresh.
        // NOTE: we do this by reloading the dashboard (rather than just retrieving the widgets data) because we want to show a loading icon when it's resetting, so user knows something has happened.
        dashboard = dashboard.resetRefreshId(); // We remove this property so the refreshed dashboard is the original one without filters applied
        dispatch(loadDashboard(dashboard));
      } else {
        // user has either clicked the refresh button, or has updated the applied filters
        dispatch(refreshDashboard(true, filtersAfter, e));
      }
    };

  const userInteraction = (props: unknown) => (e: Maybe<React.MouseEvent>) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    dispatch(modalInteraction(DASHBOARDS_VIEW, props));
  };

  if (!dashboardsState?.active) {
    return (
      <div className={flex.middle} style={{ height: '100vh' }}>
        <AjaxSpinner />
      </div>
    );
  }

  return (
    <DashboardsContainer
      isDashboardView
      ref={containerRef}
      data-testid={DASHBOARD_CONTAINER}
    >
      <Dashboard_LEGACY
        datasourceId={datasourceId}
        dashboardId={dashboardId}
        state={dashboardsState}
        restricted={restricted}
        widgetBeingEdited={widgetBeingEdited}
        userActions={userActions}
        isSelfHosted={isSelfHosted}
        outstandingWidgetRequests={dashboardsState.outstandingWidgetRequests}
        onConfigureChart={onConfigureChart}
        onOpenViewInNewTab={onOpenViewInNewTab}
        onOpenViewWithFilters={onOpenViewWithFilters}
        onSave={onSave(dashboardsState)}
        onDiscard={onDiscard}
        onAwaitingWidgetData={onAwaitingWidgetData}
        onRefreshClicked={onRefreshClicked(dashboardsState.active!)}
        userInteraction={userInteraction}
        dashboardType={PRIVATE_DASHBOARD}
      />
      <Permission permissionPath={`${datasourceId}.write`}>
        <RefreshTimer />
      </Permission>
    </DashboardsContainer>
  );
};

export default Dashboard;
